// OrdersTab — the Orders section's list surface (redesign Task 5; the F4
// amendment supersedes the T6-era chip design this file used to own).
//
// What changed and why:
// - The AC4 aria-pressed filter CHIPS are retired for ONE single-select
//   EmbedSegmented radiogroup driven by ORDER_FILTER_GROUPS below — the
//   one place the segment→statuses mapping is declared. All is a real
//   segment now (the old design's unfiltered default), "Open" groups the
//   pre-shipment statuses, and pending/refunded finally get a home.
// - The list is order CARDS (OrderCardList, ruling F4) — the retired card
//   row was a click+keydown div with role="button" and a raw enum for a
//   status label; the card is a real button link with a statusPresentation
//   badge, so list and detail can never paint the same enum twice.
// - Pagination (Review Focus 4 / TD-579): AccountCenter pages through
//   /me/orders 50 at a time, so counts are PARTIAL whenever another page
//   exists ("All 20+") and a "Load more orders" control appends the next
//   cursor's page. This component owns none of that fetch — it renders the
//   state it is handed.
// - The component renders NO page header of its own: the account shell
//   (AccountCenter) already paints the registry's section title and
//   description above this panel, and a second header here would
//   duplicate it (divergence from the plan's EmbedPageHeader sketch,
//   reported in the task report).
//
// `segmentId`/`onSegmentChange` lift the selection so AccountCenter can
// preserve the active segment across a detail-view round trip (open an
// order, come back, still be on "Shipped"). Uncontrolled callers omit both
// and get "all" as the default.
//
// ⚠️ AMENDED 2026-10-06 (account-area redesign, Task 18). Gained the
// per-store filter (`storeFilter` + `onClearStoreFilter`) — the deep link a
// store tile's "View all" control follows (the Task 6 registry-tab pattern:
// AccountCenter applies it once server-side and hands the filtered pages
// here, exactly as it does the unfiltered ones). This component still owns
// NO fetch. The filter's presentation contract:
//   - a dismissible indicator naming the store sits ABOVE the status
//     segments (the segments keep filtering on top of the server-side store
//     filter — orthogonal axes);
//   - the EMPTY state names the store ("No orders for this store yet.") —
//     the generic "No orders yet." under a store filter reads as "you have
//     no orders at all", which is false.
//
// D5: every order-card block renders only when its field is present
// (OrderCardList's contract), so this surface ships before Tasks 3/14 do.

import { useState } from "react";
import {
  useEmbedTokens,
  EmbedCard,
  EmbedText,
  EmbedButton,
  EmbedSegmented,
  EmbedEmptyState,
} from "../../embed-shared/primitives";
import type { OrderSummary, OrderStatus } from "../../embed-shared/types";
import { OrderCardList } from "./OrderCardList";

/** The active per-store filter (account-area redesign Task 18): the store
 *  whose id the server filters on, plus its display name for the indicator.
 *  The name rides along because OrdersTab owns no store lookup. */
export interface StoreOrdersFilter {
  id: string;
  name: string;
}

export type OrderFilterGroupId = "all" | "open" | "shipped" | "delivered" | "cancelled" | "refunded";

export interface OrderFilterGroup {
  id: OrderFilterGroupId;
  label: string;
  /** "all" = the unfiltered list; otherwise the statuses the segment matches. */
  statuses: OrderStatus[] | "all";
}

// The ONE place the segment mapping is declared (§55): exported and driven by
// both this component and its tests. "open" = everything before it ships.
export const ORDER_FILTER_GROUPS: OrderFilterGroup[] = [
  { id: "all", label: "All", statuses: "all" },
  { id: "open", label: "Open", statuses: ["pending", "pending_payment", "confirmed", "processing"] },
  { id: "shipped", label: "Shipped", statuses: ["shipped"] },
  { id: "delivered", label: "Delivered", statuses: ["delivered"] },
  { id: "cancelled", label: "Cancelled", statuses: ["cancelled"] },
  { id: "refunded", label: "Refund recorded", statuses: ["refunded"] },
];

export interface OrdersTabProps {
  orders: OrderSummary[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Opens the order inside the shell; passed through to each card. */
  onOpenOrder?: (orderId: string) => void;
  /** True while the server reports another page after the ones loaded. */
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** An append (Load-more) failure. Rendered BESIDE the Load-more control,
   *  never gating the list — the loaded pages stay on screen and the control
   *  itself remains the retry. Only the initial-load `error` replaces the
   *  list (review P2: append failure ≠ list failure). */
  appendError?: string | null;
  /** The empty state's next action; absent = the empty state offers none. */
  onBrowseProducts?: () => void;
  /** Controlled segment (AccountCenter lifts it across the detail round trip). */
  segmentId?: OrderFilterGroupId;
  onSegmentChange?: (id: OrderFilterGroupId) => void;
  /** The active per-store filter (Task 18 deep link from a store tile).
   *  Null/absent = the unfiltered list; the indicator never renders. */
  storeFilter?: StoreOrdersFilter | null;
  /** Clears the store filter (refetches unfiltered — the shell's job);
   *  absent = the indicator renders without a Clear control. */
  onClearStoreFilter?: () => void;
}

export function OrdersTab({
  orders,
  loading,
  error,
  onRetry,
  onOpenOrder,
  hasMore,
  loadingMore,
  onLoadMore,
  appendError,
  onBrowseProducts,
  segmentId,
  onSegmentChange,
  storeFilter,
  onClearStoreFilter,
}: OrdersTabProps) {
  const t = useEmbedTokens();
  const [internalSegment, setInternalSegment] = useState<OrderFilterGroupId>("all");

  // Controlled when a lifted value is present; otherwise local state. In the
  // controlled form a click reports the change and waits for the re-render —
  // the value never moves on its own.
  const activeSegment: OrderFilterGroupId = segmentId ?? internalSegment;
  const selectSegment = (id: string) => {
    if (onSegmentChange) onSegmentChange(id as OrderFilterGroupId);
    else setInternalSegment(id as OrderFilterGroupId);
  };

  // Counts over the LOADED page; "+" marks them partial while another page
  // exists (TD-579). A zero group is omitted — offering "Cancelled 0" is
  // noise — except All, which is the unconditional anchor of the group.
  const countFor = (g: OrderFilterGroup) =>
    g.statuses === "all" ? orders.length : orders.filter((o) => g.statuses.includes(o.status)).length;
  const options = ORDER_FILTER_GROUPS.flatMap((g) => {
    const n = countFor(g);
    if (n === 0 && g.id !== "all") return [];
    return [{ id: g.id, label: g.label, count: hasMore ? `${n}+` : n }];
  });

  // Gate advisory 5 (Task-5 review): the zero-count omission above can retire
  // the very segment a caller lifted (`segmentId`) — data changed under the
  // selection (a shipped order delivered, a refund processed), or an empty
  // list leaves only "All". Rendering `value={activeSegment}` then names an
  // option the radiogroup does not offer: NO checked radio, a radioset with
  // no selection (an ARIA violation — a single-select radiogroup always has
  // one). Fall back to "all" for list behavior AND the control's value; the
  // lifted value itself is never overwritten, so if the data returns the
  // segment it re-activates on the next render. Deliberately no
  // onSegmentChange sync: the caller's state stays authoritative.
  const effectiveSegment: OrderFilterGroupId = options.some((o) => o.id === activeSegment)
    ? activeSegment
    : "all";
  const activeGroup = ORDER_FILTER_GROUPS.find((g) => g.id === effectiveSegment) ?? ORDER_FILTER_GROUPS[0];
  const visibleOrders =
    activeGroup.statuses === "all" ? orders : orders.filter((o) => activeGroup.statuses.includes(o.status));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      {/* Task 18: the per-store filter indicator — dismissible, above the
          status segments (which keep working on top of the server-side
          filter). A plain styled row: EmbedBanner's two tones are
          error/success, and neither is the semantics of "a filter is
          active". */}
      {storeFilter && (
        <div
          data-testid="orders-store-filter"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: t.spacing.sm,
            flexWrap: "wrap",
            border: `1px solid ${t.colors.border}`,
            borderRadius: t.borderRadius,
            padding: `${t.spacing.sm}px ${t.spacing.md}px`,
          }}
        >
          <EmbedText tone="secondary">
            Showing orders for <span style={{ color: t.colors.text, fontWeight: t.fontWeight.medium }}>{storeFilter.name}</span>
          </EmbedText>
          {onClearStoreFilter && (
            <EmbedButton type="button" variant="secondary" size="sm" onClick={onClearStoreFilter}>
              Clear filter
            </EmbedButton>
          )}
        </div>
      )}

      {/* The filter is unconditional — it labels the list region in every
          state (loading, error, empty), not only once orders exist. */}
      <EmbedSegmented
        aria-label="Filter orders by status"
        options={options}
        value={effectiveSegment}
        onChange={selectSegment}
      />

      {loading && (
        <p role="status" style={{ margin: 0 }}>
          <EmbedText tone="secondary">Loading orders...</EmbedText>
        </p>
      )}

      {!loading && error && (
        <EmbedCard>
          <EmbedText tone="error">{error}</EmbedText>
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedButton type="button" variant="secondary" onClick={onRetry}>
              Retry
            </EmbedButton>
          </div>
        </EmbedCard>
      )}

      {!loading && !error && orders.length === 0 && storeFilter && (
        // The store-aware empty state (Task 18): under a store filter the
        // generic copy would read as "you have no orders at all" — false.
        // No Browse action: the catalog isn't the fix for an empty store.
        <EmbedEmptyState
          icon="orders"
          title="No orders for this store yet."
          body={`Orders with an item assigned to ${storeFilter.name} will show up here.`}
        />
      )}

      {!loading && !error && orders.length === 0 && !storeFilter && (
        <EmbedEmptyState
          icon="orders"
          title="No orders yet."
          body="Orders you place will show up here."
          action={
            onBrowseProducts ? (
              <EmbedButton type="button" variant="secondary" onClick={onBrowseProducts}>
                Browse products
              </EmbedButton>
            ) : undefined
          }
        />
      )}

      {!loading && !error && orders.length > 0 && visibleOrders.length === 0 && (
        <EmbedEmptyState icon="orders" title="No orders in this view." body="Try a different status filter." />
      )}

      {!loading && !error && visibleOrders.length > 0 && (
        <OrderCardList orders={visibleOrders} onOpenOrder={onOpenOrder ?? (() => {})} />
      )}

      {/* Append failures surface HERE — beside the control, list untouched.
          The control below stays enabled after a failure, so it is the retry. */}
      {!loading && appendError && (
        <p role="alert" style={{ margin: 0, textAlign: "center" }}>
          <EmbedText tone="error">{appendError}</EmbedText>
        </p>
      )}

      {!loading && !error && hasMore && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <EmbedButton
            type="button"
            variant="secondary"
            disabled={loadingMore}
            onClick={() => {
              if (!loadingMore) onLoadMore();
            }}
          >
            {loadingMore ? "Loading more orders..." : "Load more orders"}
          </EmbedButton>
        </div>
      )}
    </div>
  );
}
